import { baseApi } from 'core/api/base'
import { API_BASE_URL_IADOCS } from 'core/constants/general'
import {
  DebugDeleteResult,
  DebugDocumentFilters,
  DebugDocumentList,
  DebugRequeteDetailResponse,
  DebugRequeteList,
  DebugStatistique,
  DebugStatistiqueGranularity,
} from 'iadocs/interfaces/IDebug'

const appendQuery = (
  params: URLSearchParams,
  key: string,
  value: string | number | null | undefined
) => {
  if (value === null || value === undefined || value === '') {
    return
  }
  params.set(key, String(value))
}

const appendList = (params: URLSearchParams, key: string, values: string[]) => {
  values.forEach((value) => {
    if (value) {
      params.append(`${key}[]`, value)
    }
  })
}

export const buildDocumentQuery = (args: {
  page: number
  perPage: number
  filters: DebugDocumentFilters
}) => {
  const params = new URLSearchParams()
  appendQuery(params, 'page', args.page)
  appendQuery(params, 'perPage', args.perPage)
  appendQuery(params, 'hash', args.filters.hash)
  appendList(params, 'nomDocuments', args.filters.nomDocuments)
  appendQuery(params, 'dateOperator', args.filters.dateOperator)
  appendQuery(params, 'dateMonth', args.filters.dateMonth)
  return params.toString()
}

export const buildRequeteQuery = (args: {
  page: number
  perPage: number
  filters: {
    statutTech: string | null
    typeDocuments: string[]
    partenaire: string | null
    origine: string | null
    idDossier: string | null
    idDossierPartenaire: string | null
  }
}) => {
  const params = new URLSearchParams()
  appendQuery(params, 'page', args.page)
  appendQuery(params, 'perPage', args.perPage)
  appendQuery(params, 'statutTech', args.filters.statutTech)
  appendList(params, 'typeDocuments', args.filters.typeDocuments)
  appendQuery(params, 'partenaire', args.filters.partenaire)
  appendQuery(params, 'origine', args.filters.origine)
  appendQuery(params, 'idDossier', args.filters.idDossier)
  appendQuery(params, 'idDossierPartenaire', args.filters.idDossierPartenaire)
  return params.toString()
}

const DebugApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    debugDocuments: builder.query<
      DebugDocumentList,
      { page: number; perPage: number; filters: DebugDocumentFilters }
    >({
      query: (args) => ({
        url: `${API_BASE_URL_IADOCS}/debug/documents?${buildDocumentQuery(args)}`,
      }),
      providesTags: ['DebugDocument'],
    }),
    debugDeleteDocuments: builder.mutation<
      DebugDeleteResult,
      { ids: number[]; filters: DebugDocumentFilters }
    >({
      query: (body) => ({
        url: `${API_BASE_URL_IADOCS}/debug/documents/delete`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DebugDocument'],
    }),
    debugRequetes: builder.query<
      DebugRequeteList,
      {
        page: number
        perPage: number
        filters: {
          statutTech: string | null
          typeDocuments: string[]
          partenaire: string | null
          origine: string | null
          idDossier: string | null
          idDossierPartenaire: string | null
        }
      }
    >({
      query: (args) => ({
        url: `${API_BASE_URL_IADOCS}/debug/requetes?${buildRequeteQuery(args)}`,
      }),
      providesTags: ['DebugRequete'],
    }),
    debugRequeteDetail: builder.query<DebugRequeteDetailResponse, number>({
      query: (id) => ({
        url: `${API_BASE_URL_IADOCS}/debug/requetes/${id}`,
      }),
      providesTags: ['DebugRequete'],
    }),
    debugStatistiques: builder.query<
      DebugStatistique,
      { from: string; to: string; granularity: DebugStatistiqueGranularity }
    >({
      query: ({ from, to, granularity }) => ({
        url: `${API_BASE_URL_IADOCS}/debug/statistiques?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&granularity=${granularity}`,
      }),
    }),
  }),
})

export const {
  useDebugDocumentsQuery,
  useDebugDeleteDocumentsMutation,
  useDebugRequetesQuery,
  useDebugRequeteDetailQuery,
  useDebugStatistiquesQuery,
} = DebugApi
